'use client';

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import type { CourseWithBundle } from '@peach/types/courses/course-public.dto.js';
import { tw } from '@tw';
import { bookClass } from '@/app/actions/bookings';
import { updateProfile } from '@/app/actions/profile';
import { callNoun } from '@/lib/utils/bookable-product';
import { formatDate } from '@/lib/utils/date';
import { useCalendarStore } from '@/lib/stores/calendar.store';
import { SlotPicker } from './SlotPicker';
import { passFinePrint } from './policies';
import { callAction } from '@/lib/call-action';

const PASS_TICKET =
  'rounded-[var(--sf-radius)] border-[1.5px] border-dashed border-[var(--sf-line)] bg-[var(--sf-ticket)] px-3.5 py-3';
const PASS_TITLE = 'block sf-title text-title-2 font-extrabold text-[var(--sf-ink)]';
const PASS_DOT = 'h-[5px] w-[14px] rounded-full';

interface PassCardProps {
  course: CourseWithBundle;
  tutorUsername: string;
  tutorName: string;
  /** The page clock, straight down to the picker. */
  serverNow: number;
  userProfile: { name: string | null; username: string | null } | null;
}

export function PassCard({
  course,
  tutorUsername,
  tutorName,
  serverNow,
  userProfile,
}: PassCardProps) {
  const router = useRouter();
  const [nameInput, setNameInput] = useState('');
  const [nameSaved, setNameSaved] = useState(false);
  const needsName = !userProfile?.name && !nameSaved;
  const [justBooked, setJustBooked] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(course.available);
  const calendarChanged = useCalendarStore((s) => s.changed);

  const spent = remaining <= 0;

  useEffect(() => {
    if (!justBooked) return;
    let tries = 0;
    let timer = 0;
    const reveal = () => {
      const el = document.getElementById(`sf-call-${justBooked}`);
      if (el) {
        document.querySelectorAll('.sf-fresh').forEach((prev) => prev.classList.remove('sf-fresh'));
        el.classList.add('sf-fresh');
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (++tries < 40) timer = window.setTimeout(reveal, 100);
    };
    reveal();
    return () => window.clearTimeout(timer);
  }, [justBooked]);

  const handleBook = useCallback(
    async (isoTime: string, timezone: string) => {
      if (needsName) {
        const trimmed = nameInput.trim();
        if (trimmed.length < 2) return 'Add your name so the provider knows who booked';
        const saved = await callAction(() => updateProfile({ name: trimmed }));
        if (!saved.success) return 'Could not save your name — try again';
        setNameSaved(true);
      }

      const result = await callAction(() =>
        bookClass(tutorUsername, course.bundle.id, isoTime, timezone, course.id)
      );
      if (!result.success) return result.error;

      setJustBooked(result.data.classId);
      setRemaining((r) => Math.max(0, r - 1));
      calendarChanged(tutorUsername);
      router.refresh();
    },
    [needsName, nameInput, tutorUsername, course.bundle.id, course.id, router, calendarChanged]
  );

  const title = course.bundle.title || `${course.total} × ${callNoun(course.total)}`;

  return (
    <div id={`sf-pass-${course.id}`} className={PASS_TICKET}>
      <span className={PASS_TITLE}>
        {/* An untouched pass states the PURCHASE; once a call is spent the
            card switches to the remainder for good. Read off the state rather
            than a just-bought flag: the flag dies on the next load, and this
            sentence should stand until the pass is actually used. Never both —
            the dots below already say used against total. */}
        {remaining === course.total
          ? `You bought ${remaining} ${callNoun(remaining)}`
          : `${remaining} ${callNoun(remaining)} left`}
      </span>
      <span className="sf-eyebrow mt-0.5 flex items-center justify-between gap-2 text-badge font-semibold text-[var(--sf-fnt)]">
        <span className="truncate">{title}</span>
        <span className="flex-shrink-0">expires {formatDate(course.expiresAt, 'MMM D')}</span>
      </span>
      <span
        className="mt-1.5 flex gap-1"
        aria-label={`${remaining} of ${course.total} ${callNoun(course.total)} left`}
      >
        {Array.from({ length: course.total }, (_, i) => (
          <span
            key={i}
            className={tw(
              PASS_DOT,
              i < course.total - remaining ? 'bg-[var(--sf-line)]' : 'bg-[var(--sf-lime)]'
            )}
          />
        ))}
      </span>

      {spent ? (
        <div className="mt-2.5 flex flex-col gap-2">
          <span className="text-p-3 text-[var(--sf-mut)]">All calls used</span>
          <a href="#sf-products" className="mm-btn sf-cta sf-cta-line">
            Get another pack
          </a>
        </div>
      ) : (
        <SlotPicker
          tutorUsername={tutorUsername}
          calendar={{ bundleId: course.bundle.id }}
          empty={`No times open yet — your pass is safe and waits until ${tutorName} opens times`}
          serverNow={serverNow}
          busyLabel="Booking…"
          label={(time) =>
            time
              ? `Book ${time.format('ddd, MMM D')} at ${time.format('HH:mm')}`
              : 'Pick a day and time'
          }
          extra={
            needsName ? (
              <input
                type="text"
                autoComplete="name"
                placeholder="Your name — so they know who booked"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                className="rounded-[10px] border-[1.5px] border-[var(--sf-line)] bg-[var(--sf-card)] px-3.5 py-2.5 text-[16px] text-[var(--sf-ink)] outline-none placeholder:text-[var(--sf-fnt)] sm:text-p-3"
              />
            ) : null
          }
          onPick={handleBook}
        />
      )}

      {/* A spent pass has nothing left to refund, and booking the last session
          flips `spent` here before the refresh lands — the note would sit under
          "All calls used" saying the calls are still theirs. */}
      {!spent && (
        <span className="sf-note mt-2.5 block">
          {passFinePrint(formatDate(course.expiresAt, 'MMM D'))}
        </span>
      )}
    </div>
  );
}
